import { Component } from '@angular/core';
import { UntypedFormBuilder, Validators, FormGroup } from '@angular/forms';
import { createErrorControlMap, ErrorControl } from '@dxo/ng-swift-ui';
import { DropdownOption } from '@benefitsui/app/lib/common.types';
import { Constants } from '../../billing-page/billing-page.types';

@Component({
  selector: 'app-add-employee',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.scss']
})
export class AddEmployeeComponent {
  constructor(private fb: UntypedFormBuilder) {
    this.addEmployeeForm = this.fb.group({
      firstName: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(this.NAME_MIN_LENGTH),
          Validators.maxLength(this.NAME_MAX_LENGTH)
        ]
      ],
      middleName: [
        Constants.StringEmpty,
        [
          Validators.minLength(this.NAME_MIN_LENGTH),
          Validators.maxLength(this.NAME_MAX_LENGTH)
        ]
      ],
      lastName: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.minLength(this.NAME_MIN_LENGTH),
          Validators.maxLength(this.NAME_MAX_LENGTH)
        ]
      ],
      ssn: [
        Constants.StringEmpty,
        [
          Validators.required,
          Validators.pattern(this.ssnPatternRegex),
          Validators.maxLength(11)
        ]
      ],
      birthDate: [
        Constants.StringEmpty,
        [Validators.required, Validators.pattern(this.dateFormatRegex)]
      ],
      gender: [Constants.StringEmpty, [Validators.required]],
      hireDate: [
        Constants.StringEmpty,
        [Validators.required, Validators.pattern(this.dateFormatRegex)]
      ],
      smokerStatus: [Constants.StringEmpty],
      occupation: [Constants.StringEmpty],
      annualSalary: [Constants.StringEmpty, [Validators.required]],
      billDivision: [Constants.StringEmpty, [Validators.required]]
    });

    this.formErrors = {
      ...this.getErrorMap(this.addEmployeeForm)
    };
  }

  // :  Data members
  addEmployeeForm: FormGroup;
  formErrors: { [key: string]: ErrorControl };

  steps = [
    {
      label: 'Step 1',
      active: true,
      disabled: false
    },
    {
      label: 'Step 2',
      active: false,
      disabled: false
    },
    {
      label: 'Step 3',
      active: false,
      disabled: false
    }
  ];

  onStepClick(stepIndex: number): void {
    this.steps.forEach((s, index) => (s.active = index === stepIndex));
  }

  genderListOptions: DropdownOption<string>[] = [
    { key: 'male', displayName: 'Male' },
    { key: 'female', displayName: 'Female' },
    { key: 'non-binary', displayName: 'Non-Binary' },
    { key: 'unknown', displayName: 'Unknown' }
  ];

  smokerStatusOptions: DropdownOption<string>[] = [
    { key: 'yes', displayName: 'Yes' },
    { key: 'no', displayName: 'No' }
  ];

  billDivisionOptions: DropdownOption<string>[] = [
    { key: 'division1', displayName: 'License to Bil' },
    { key: 'division2', displayName: 'Bill & Teds Excellent Adventure' },
    { key: 'division3', displayName: 'The Day the Earth Stood Bill' }
  ];

  employeeCardTitle = 'Employee Information';
  hireDateError = 'Hire date must occur after birth date';
  dateFormatMessage = 'Date format must be MM/DD/YYYY';
  NAME_MIN_LENGTH = 2;
  NAME_MAX_LENGTH = 50;
  primaryButtonText = 'Back to Search Employees';
  secondaryButtonText = 'Save and Continue';
  requiredErrorMessage = `This field is required`;
  ssnPatternMessage = 'SSN must be in the format XXX-XX-XXXX';
  ssnPatternRegex = '^(?!666|000|9\\d{2})\\d{3}-(?!00)\\d{2}-(?!0{4})\\d{4}$';
  dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[01])\/\d{4}$/;

  employeeData = {
    firstName: {
      data: Constants.StringEmpty,
      label: 'First Name'
    },
    middleName: {
      data: Constants.StringEmpty,
      label: 'Middle'
    },
    lastName: {
      data: Constants.StringEmpty,
      label: 'Last Name'
    },
    ssn: {
      data: Constants.StringEmpty,
      label: 'SSN',
      placeholder: 'XXX-XX-XXXX'
    },
    birthDate: {
      data: Constants.StringEmpty,
      label: 'Birth Date'
    },
    gender: {
      data: ['Male', 'Female', 'Non-Binary', 'Other'],
      label: 'Gender'
    },
    hireDate: {
      data: Constants.StringEmpty,
      label: 'Hire Date'
    },
    smokerStatus: {
      data: ['Yes', 'No'],
      label: 'Smoker Status'
    },
    occupation: {
      data: Constants.StringEmpty,
      label: 'Occupation'
    },
    annualSalary: {
      data: Constants.StringEmpty,
      label: 'Annual Salary'
    },
    billDivision: {
      data: [
        'Division 1',
        'Division 2',
        'Division 3',
        'Division 4',
        'Division 5'
      ],
      label: 'Bill Division'
    }
  };

  getErrorMap = (formGroup: FormGroup) =>
    createErrorControlMap(formGroup, {
      firstName: {
        required: this.requiredErrorMessage,
        maxlength: `Entry must not exceed ${this.NAME_MAX_LENGTH} characters`,
        minlength: `Entry must be at least ${this.NAME_MIN_LENGTH} characters`
      },
      lastName: {
        required: this.requiredErrorMessage,
        maxlength: `Entry must not exceed ${this.NAME_MAX_LENGTH} characters`,
        minlength: `Entry must be at least ${this.NAME_MIN_LENGTH} characters`
      },
      ssn: {
        required: this.requiredErrorMessage,
        pattern: this.ssnPatternMessage
      },
      birthDate: {
        required: this.requiredErrorMessage,
        pattern: this.dateFormatMessage
      },
      hireDate: {
        required: this.requiredErrorMessage,
        pattern: this.dateFormatMessage
      },
      gender: {
        required: this.requiredErrorMessage
      },
      annualSalary: {
        required: this.requiredErrorMessage
      },
      billDivision: {
        required: this.requiredErrorMessage
      }
    }) as object as {
      firstName: ErrorControl;
      lastName: ErrorControl;
      ssn: ErrorControl;
      birthDate: ErrorControl;
      hireDate: ErrorControl;
      gender: ErrorControl;
      annualSalary: ErrorControl;
      billDivision: ErrorControl;
    };

  formatSSN(value: string): string {
    const v = value.replace(/\D/g, Constants.StringEmpty).slice(0, 9);
    const arr = v.match(/(\d{1,3})(\d{1,2})?(\d{1,4})?/);
    if (arr) {
      return `${arr[1] || Constants.StringEmpty}${arr[2] ? '-' + arr[2] : Constants.StringEmpty}${arr[3] ? '-' + arr[3] : Constants.StringEmpty}`;
    }
    return Constants.StringEmpty;
  }

  onSSNInputChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const formattedSSN = this.formatSSN(inputValue);
    this.addEmployeeForm.controls['ssn'].setValue(formattedSSN, {
      emitEvent: false
    });
  }

  submitForm(): void {
    if (this.addEmployeeForm.valid) {
      console.log('Form VALID');
    } else {
      console.log('Form INVALID');
    }
  }
}
